/**
 * Shopify uploaded videos expose an m3u8 (HLS) source alongside mp4.
 * Safari uses native HLS when possible; other browsers load hls.js on demand.
 * A poster layer stays above the video until frames are ready and playback has started.
 */

const HLS_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.7/dist/hls.min.js';

/** @type {Promise<void> | null} */
let hlsScriptPromise = null;

/** @type {WeakMap<Element, object>} */
const hlsInstances = new WeakMap();

/** @type {boolean} */
let sectionUnloadBound = false;

/**
 * Loads hls.js from CDN once and returns a shared promise.
 * @returns {Promise<void>}
 */
function loadHlsLibrary() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (window.Hls) {
    return Promise.resolve();
  }
  if (hlsScriptPromise) {
    return hlsScriptPromise;
  }
  hlsScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = HLS_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load hls.js'));
    document.head.appendChild(script);
  });
  return hlsScriptPromise;
}

/**
 * Returns true when the browser can play HLS in a video element without MSE/hls.js.
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
function canPlayNativeHls(video) {
  const t = video.canPlayType('application/vnd.apple.mpegurl');
  return t === 'probably' || t === 'maybe';
}

/**
 * Ensures a protocol-relative URL is usable as a full URL string.
 * @param {string} url
 * @returns {string}
 */
function absoluteMediaUrl(url) {
  if (!url) {
    return '';
  }
  // Avoid writing "//" inside quotes on one line; Vite's import analyser can treat it as a comment.
  const protocolRelative = '/' + '/';
  if (url.slice(0, 2) === protocolRelative) {
    return `https:${url}`;
  }
  return url;
}

/**
 * Hides the poster overlay once the stream is far enough along to avoid a black flash.
 * @param {HTMLVideoElement} video
 * @param {Element | null} poster
 */
function bindPosterUntilPlaying(video, poster) {
  if (!poster) {
    return;
  }

  let canRemovePoster = false;
  let hasPlayed = false;

  const tryHidePoster = () => {
    if (canRemovePoster && hasPlayed) {
      window.setTimeout(() => {
        poster.classList.add('is-playing');
      }, 150);
    }
  };

  video.addEventListener('loadeddata', () => {
    canRemovePoster = true;
    tryHidePoster();
  });

  video.addEventListener('playing', () => {
    hasPlayed = true;
    tryHidePoster();
  });

  video.addEventListener(
    'timeupdate',
    () => {
      if (video.currentTime > 0) {
        canRemovePoster = true;
        hasPlayed = true;
        tryHidePoster();
      }
    },
    { once: true }
  );
}

/**
 * Observes visibility to play/pause and sync dataset used after manifest parse.
 * @param {Element} wrapper
 * @param {HTMLVideoElement} video
 */
function observePlayPause(wrapper, video) {
  const playObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio >= 0.01) {
          wrapper.dataset.isVisible = 'true';
          video.muted = true;
          video.play().catch(() => {});
        } else {
          wrapper.dataset.isVisible = 'false';
          video.pause();
          const poster = wrapper.querySelector('.video-section__poster');
          poster?.classList.remove('is-playing');
        }
      });
    },
    { threshold: [0, 0.01, 0.25, 0.5] }
  );
  playObserver.observe(wrapper);
}

/**
 * Creates and wires a muted autoplay video for a Shopify HLS wrapper.
 * @param {{ wrapper: Element; hlsUrl: string; mp4Url: string; loop: boolean; posterUrl: string }} options
 * @returns {Promise<void>}
 */
async function setupVideoInWrapper(options) {
  const { wrapper, hlsUrl, mp4Url, loop, posterUrl } = options;
  const mount = wrapper.querySelector('.js-video-hls-mount');
  const poster = wrapper.querySelector('.video-section__poster');

  if (!mount) {
    return;
  }

  const video = document.createElement('video');
  video.className = 'video-section__video';
  video.muted = true;
  video.loop = loop;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.controls = true;
  video.preload = 'auto';

  if (posterUrl) {
    video.setAttribute('poster', posterUrl);
  }

  mount.appendChild(video);
  bindPosterUntilPlaying(video, poster);

  const hlsAbs = absoluteMediaUrl(hlsUrl);
  const mp4Abs = absoluteMediaUrl(mp4Url);

  if (canPlayNativeHls(video)) {
    const sHls = document.createElement('source');
    sHls.src = hlsAbs;
    sHls.type = 'application/vnd.apple.mpegurl';
    video.appendChild(sHls);
    if (mp4Abs) {
      const sMp4 = document.createElement('source');
      sMp4.src = mp4Abs;
      sMp4.type = 'video/mp4';
      video.appendChild(sMp4);
    }
    video.load();
    return;
  }

  try {
    await loadHlsLibrary();
  } catch {
    // Script failed; fall through to mp4 only when available.
  }

  const HlsGlobal = typeof window !== 'undefined' ? window.Hls : null;

  if (HlsGlobal && HlsGlobal.isSupported()) {
    const hls = new HlsGlobal({
      maxBufferLength: 10,
      maxMaxBufferLength: 20,
      maxBufferSize: 20 * 1000 * 1000,
      maxBufferHole: 0.3,
      manifestLoadingTimeOut: 5000,
      levelLoadingTimeOut: 5000,
      fragLoadingTimeOut: 10000,
      abrEwmaDefaultEstimate: 500000 * 5,
      enableWorker: false
    });

    hls.on(HlsGlobal.Events.ERROR, (event, data) => {
      if (!data.fatal) {
        return;
      }
      if (data.type === HlsGlobal.ErrorTypes.NETWORK_ERROR) {
        hls.startLoad();
      } else if (data.type === HlsGlobal.ErrorTypes.MEDIA_ERROR) {
        hls.recoverMediaError();
      } else {
        hls.destroy();
      }
    });

    hlsInstances.set(wrapper, hls);
    hls.loadSource(hlsAbs);
    hls.attachMedia(video);

    hls.on(HlsGlobal.Events.MANIFEST_PARSED, () => {
      if (wrapper.dataset.isVisible === 'true') {
        video.muted = true;
        video.play().catch(() => {});
      }
    });
    return;
  }

  if (mp4Abs) {
    video.src = mp4Abs;
    video.load();
  }

  video.addEventListener('click', () => {
    video.controls = true;
    video.muted = false;
    video.play().catch(() => {});
  });
}

/**
 * Binds click-to-unmute once the video element exists.
 * @param {HTMLVideoElement} video
 */
function bindUnmuteOnClick(video) {
  video.addEventListener('click', () => {
    video.controls = true;
    video.muted = false;
    video.play().catch(() => {});
  });
}

/**
 * Initialises all `.js-shopify-hls` wrappers: preload with a generous root margin, then attach stream.
 */
export function initShopifyHlsAutoplayVideos() {
  const wrappers = document.querySelectorAll('.js-shopify-hls');

  wrappers.forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) {
      return;
    }
    wrapper.dataset.isVisible = 'false';

    const hlsUrl = wrapper.dataset.hlsSrc || '';
    const mp4Url = wrapper.dataset.mp4Src || '';
    const loop = wrapper.dataset.videoLoop === 'true';
    const posterUrl = wrapper.dataset.posterSrc || '';

    if (!hlsUrl) {
      return;
    }

    let initStarted = false;

    const preloadObserver = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || initStarted) {
            return;
          }
          initStarted = true;
          obs.unobserve(entry.target);

          const run = async () => {
            const needsMse = (() => {
              const v = document.createElement('video');
              return !canPlayNativeHls(v);
            })();

            if (needsMse) {
              try {
                await loadHlsLibrary();
              } catch {
                // Hls.js failed to load; setupVideoInWrapper will fall back to mp4 only.
              }
            }

            await setupVideoInWrapper({
              wrapper,
              hlsUrl,
              mp4Url,
              loop,
              posterUrl
            });

            const video = wrapper.querySelector(':scope > .js-video-hls-mount > video');
            if (video) {
              bindUnmuteOnClick(video);
              observePlayPause(wrapper, video);
              if (wrapper.dataset.isVisible === 'true') {
                video.muted = true;
                video.play().catch(() => {});
              }
            }
          };

          run();
        });
      },
      { rootMargin: '400px 0px 200px 0px', threshold: 0 }
    );

    preloadObserver.observe(wrapper);
  });

  if (!sectionUnloadBound) {
    sectionUnloadBound = true;
    document.addEventListener('shopify:section:unload', (event) => {
      const targets = event.target.querySelectorAll?.('.js-shopify-hls') || [];
      targets.forEach((el) => {
        const hls = hlsInstances.get(el);
        if (hls) {
          hls.destroy();
          hlsInstances.delete(el);
        }
      });
    });
  }
}
